import { Button } from 'antd'
import React, { useCallback, useRef } from 'react'
import ReactDOM from 'react-dom';
import { useStore } from '../../store/store';
import { getBackgroundBorderStyle, iconButtonStyle, findCheckboxStyle, navbarButtonStyle } from '../../utils/styles';
import { Separator } from '../Layout/Separator';
import { NavbarListButton } from '../Layout/NavbarListButton';
import DuotoneLightIcon from './DuotoneLightIcon';
import { useTranslation } from 'react-i18next';
import { dispostionButtons, ItemTypes, viewerTypes } from '../../constants/constants';
import NavbarSelector from '../Layout/NavbarSelector';
import { CaretDownOutlined } from '@ant-design/icons';
import CheckboxCustom from './CheckboxCustom';
import { leftShownStatus, useOverlay } from '../../contexts/OverlayContext';
import { isAVariableBox } from '../../utils/boxes';
import { Editor } from 'draft-js';
import { useMenuPosition, useStyleMap } from '../../hooks';
import FloatyStylingWrapper from '../Table/FloatyStylingWrapper';
import HideInFormSwitcher from './HideInFormSwitcher';
import BoxActionsPanel from './BoxActionsPanel';
import LabelledRow from './LabelledRow';
import StackedLabelField from './StackedLabelField';
import BoxFormControlsPortal from '../RightSide/BoxFormControlsPortal';
import BoxVariableFormSection from '../RightSide/BoxVariableFormSection';
import { getBlockStyle } from '../RichEditor/utils';

const selector = (box) =>
  ({ updateBox, selectedBoxId, configuration, tmp, currentDocument }) => {

    return {
      isSelected: box.isSelected ?? selectedBoxId === box.id,
      updateBox: box.updateBox ?? updateBox,
      configuration,
      checkboxStyle: findCheckboxStyle(configuration, tmp, box?.checkboxStyleId),
      // Les paramètres de formulaire se définissent au modèle : en proposition,
      // on répond au formulaire, on ne le redéfinit pas.
      isTemplate: currentDocument.type === 'template'
    };
  };

// `variant='panel'` : section de l'inspecteur de droite, un libellé par
// réglage. `inline` : barre flottante d'une box simulée, qui garde la version
// étroite (icônes seules + séparateur).
const StylingComponent = ({ updateBox, box, configuration, checkboxStyle, hasOptionsAfter = true, includeFormSwitcher = true, variant = 'inline' }) => {

  const { t } = useTranslation();
  const isPanel = variant === 'panel';

  const changeDisposition = (disposition) => {
    updateBox(box.id, (box) => {
      box.disposition = disposition
    })
  }

  const dispositionButton = (
    <NavbarListButton
      title={t('disposition')}
      icon={<DuotoneLightIcon name='grid-dividers' style={iconButtonStyle()} />}
    >
      {Object.keys(dispostionButtons).map((buttonName) => {
        return (
          <Button
            key={`${buttonName}`}
            style={{
              ...navbarButtonStyle,
              marginBottom: '1px',
            }}
            disabled={false}
            icon={dispostionButtons[buttonName].icon}
            onClick={() => changeDisposition(dispostionButtons[buttonName].disposition)}
          />
        );
      })}
    </NavbarListButton>
  );

  const styleSelector = (
    <NavbarSelector
      tooltipContent={t('checkbox-style')}
      suffixIcon={<CaretDownOutlined />}
      width={isPanel ? '100%' : 120}
      onSelect={(e) => {
        updateBox(box.id, (box) => {
          box.checkboxStyleId = configuration.checkboxStyles.find((ts) => ts.name === e).id;
        });
      }}
      selectedValue={checkboxStyle?.name}
      dropdownStyle={{ zIndex: 9999 }}
      options={configuration.checkboxStyles.map(style => ({ value: style.name, label: style.name }))}
    />
  );

  if (isPanel) {
    return (
      <>
        <LabelledRow label={t('disposition')}>{dispositionButton}</LabelledRow>
        <StackedLabelField label={t('checkbox-style')}>{styleSelector}</StackedLabelField>
      </>
    );
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      {includeFormSwitcher && box.type !== ItemTypes.QUESTION && <HideInFormSwitcher boxId={box.id} />}
      {dispositionButton}
      {styleSelector}
      {hasOptionsAfter && <Separator />}
    </div>
  )
}

const CheckboxsVariable = ({ box }) => {
  const { t } = useTranslation();
  const {
    checkboxs,
    maxNumberOfAnwser
  } = box
  const { updateLeftShownStatus, overlayProps } = useOverlay();
  const shownStatus = overlayProps?.left?.shownStatus
  const hasCheckedMaxNumberOfAnswers = checkboxs.filter((c) => c.checked).length === maxNumberOfAnwser
  const ref = useRef()
  const menuPosition = useMenuPosition(ref)

  const {
    updateBox,
    isSelected,
    configuration,
    checkboxStyle,
    isTemplate
  } = useStore(selector(box));
  const styleMap = useStyleMap();

  const getStyleOnCheckbox = useCallback(() => {
    return {
      width: checkboxStyle?.box?.width,
      height: checkboxStyle?.box?.height,
      backgroundColor: checkboxStyle?.box?.backgroundColor.style,
      border: `${checkboxStyle?.borders?.size}px solid ${checkboxStyle?.borders?.color?.style}`,
      borderRadius: `${checkboxStyle?.borders?.radius}px`,
      color: checkboxStyle?.check?.color?.style,
      fontSize: `${checkboxStyle?.check?.height}px`,
      fontWeight: `${checkboxStyle?.check?.weight}`,
    }
  }, [checkboxStyle])

  const renderCheckbox = ({ checked, label }, idx) => {
    return <div key={idx} style={{
      display: 'flex',
      columnGap: 8,
      overflow: 'hidden',
      alignItems: 'center',
      zIndex: 1000,
    }}>
      <CheckboxCustom
        label={<Editor
          readOnly
          customStyleMap={styleMap}
          blockStyleFn={getBlockStyle}
          editorState={label} />} getStyleOnCheckbox={getStyleOnCheckbox}
        checked={checked}
        disabled={hasCheckedMaxNumberOfAnswers && !checked && maxNumberOfAnwser !== 1}
        box={box}
        idx={idx}
        maxNumberOfAnwser={maxNumberOfAnwser}
      />
    </div>
  }

  const getStyle = () => {
    const keys = [
      'textDecoration',
      'fontStyle'
    ]

    const style = { ...checkboxStyle ? checkboxStyle : configuration.checkboxStyles[0] }
    keys.forEach((key) => {
      if (style[key]) {
        delete style[key]
      }
    })

    let gridTemplateColumns = "";
    let dispo;
    if (box.disposition) dispo = box.disposition
    else dispo = style.disposition
    for (let i = 0; i < dispo; i++) {
      gridTemplateColumns += "1fr "
    }
    return {
      display: "grid",
      gridTemplateColumns,
      ...getBackgroundBorderStyle(style.backgroundColor, style.borders, style),
      color: style.color.style,
    }
  }

  return (
    <>
      <div style={{
        ...getStyle(),
        width: box.width,
      }}
        ref={ref}
        onDoubleClick={(event) => {
          if (isAVariableBox(box?.type) && !box.isBoxSimulated) {
            updateLeftShownStatus(leftShownStatus["VARIABLES"], box.id)
          }
          event.stopPropagation()
        }}
      >
        {checkboxs?.map((c, idx) => renderCheckbox(c, idx))}
      </div>
      {isSelected && !box.isBoxSimulated && (shownStatus !== "variables" || box.fromViewer === viewerTypes.VARIABLES) && (
        <>
          {/* Disposition et style partaient dans la barre du haut, alors que
              le reste des réglages de la box vit dans l'inspecteur de droite. */}
          <BoxFormControlsPortal title={t('style')}>
            <StylingComponent updateBox={updateBox} box={box} configuration={configuration} checkboxStyle={checkboxStyle} variant='panel' />
          </BoxFormControlsPortal>
          {box.type !== ItemTypes.QUESTION && (
            <BoxFormControlsPortal title={t('display')}>
              <HideInFormSwitcher boxId={box.id} variant='panel' />
            </BoxFormControlsPortal>
          )}
          {/* Pas pour une box rendue dans l'aperçu du formulaire : celui-ci
              édite déjà les mêmes champs à gauche, et deux formulaires sur une
              même box se réécrivent l'un l'autre à la sortie de champ. */}
          {isTemplate && !box.fromViewer && <BoxVariableFormSection box={box} />}
          <BoxActionsPanel />
        </>
      )}
      {isSelected && box.isBoxSimulated && box.withMenu &&
        ReactDOM.createPortal(
          <FloatyStylingWrapper menuPosition={menuPosition} >
            <StylingComponent updateBox={updateBox} box={box} configuration={configuration} checkboxStyle={checkboxStyle} hasOptionsAfter={false} />
          </FloatyStylingWrapper>,
          document.getElementById('ComponentPortal')
        )}
    </>
  )
}

export default CheckboxsVariable
